import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Tag, Typography, Space, Button, Modal, Form, InputNumber, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import request from '@/utils/request';

interface CreatorRow {
  id: number;
  email: string;
  displayName: string | null;
  experimentBalanceCents: number;
  earningsBalanceCents: number;
  realNameVerified: boolean;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

const fmtCents = (v: number) => `¥${(v / 100).toFixed(2)}`;

const CreatorListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CreatorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');

  // Topup modal state
  const [topupCreator, setTopupCreator] = useState<CreatorRow | null>(null);
  const [form] = Form.useForm<{ amountRmb: number; note?: string }>();
  const [topupLoading, setTopupLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: CreatorRow[]; total: number }>('/admin/creators', {
        params: { page, pageSize, keyword },
      });
      const data = res.data as unknown as { items: CreatorRow[]; total: number };
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, keyword]);

  const onTopup = async () => {
    if (!topupCreator) return;
    try {
      const values = await form.validateFields();
      setTopupLoading(true);
      await request.post(`/admin/creators/${topupCreator.id}/topup-experiment`, {
        amountCents: Math.round(values.amountRmb * 100),
        note: values.note,
      });
      message.success('充值成功');
      setTopupCreator(null);
      form.resetFields();
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setTopupLoading(false);
    }
  };

  const columns: ColumnsType<CreatorRow> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '邮箱', dataIndex: 'email' },
    { title: '昵称', dataIndex: 'displayName', render: (v) => v || '-' },
    {
      title: '实验金',
      dataIndex: 'experimentBalanceCents',
      render: (v: number) => <Tag color="gold">{fmtCents(v)}</Tag>,
    },
    {
      title: '收益',
      dataIndex: 'earningsBalanceCents',
      render: (v: number) => <Tag color="green">{fmtCents(v)}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (v) => (
        <Tag color={v === 'active' ? 'green' : v === 'frozen' ? 'orange' : 'red'}>{v}</Tag>
      ),
    },
    {
      title: '上次登录',
      dataIndex: 'lastLoginAt',
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      render: (v) => dayjs(v).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      render: (_, row) => (
        <Space>
          <Button size="small" type="link" onClick={() => navigate(`/creators/${row.id}`)}>
            详情
          </Button>
          <Button size="small" type="link" onClick={() => setTopupCreator(row)}>
            充值
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          创作者管理
        </Typography.Title>
        <Input.Search
          allowClear
          placeholder="按邮箱或昵称搜索"
          style={{ width: 320 }}
          onSearch={(v) => {
            setPage(1);
            setKeyword(v);
          }}
        />
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
      />
      <Modal
        title={`为 ${topupCreator?.email} 充值实验金`}
        open={!!topupCreator}
        onOk={onTopup}
        confirmLoading={topupLoading}
        onCancel={() => setTopupCreator(null)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="金额（元，支持负数调减）"
            name="amountRmb"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber step={1} min={-10000} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="备注" name="note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default CreatorListPage;
